import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, ArrowRight, Shield, CreditCard, Users } from 'lucide-react';

const Insurance = () => {
  const [formData, setFormData] = useState({
    insuranceProvider: '',
    memberId: '',
    state: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Coverage check submitted:', formData);
    alert('Coverage check submitted! (This is a mock response)');
  };

  const insuranceProviders = [
    "Aetna", "Anthem", "Blue Cross Blue Shield", "Cigna", "Humana",
    "Kaiser Permanente", "Medicaid", "Medicare", "UnitedHealthcare", "Other"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-[#12324A] mb-6">
                Insurance & Medicaid
              </h1>
              <p className="text-xl text-[#5F6C7B] mb-8 leading-relaxed">
                We accept most major insurance plans and Medicaid in select states. Use your existing coverage for convenient telehealth visits.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-[#0F4C81] hover:bg-[#12324A] text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
                >
                  Check Coverage
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
                >
                  Start a Visit
                </Button>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <Shield className="text-[#1FB6B2]" size={40} />
                <CreditCard className="text-[#0F4C81]" size={40} />
                <Users className="text-[#F3A7B6]" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-[#12324A] text-center mb-4">
                Your Insurance Works Here
              </h3>
              <p className="text-[#5F6C7B] text-center">
                Standard copays apply. We bill your insurance directly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Accepted Insurance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Accepted Insurance Plans
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              We work with most major insurance providers
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
            {insuranceProviders.map((provider, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-[#1FB6B2] hover:shadow-lg transition-all duration-300 flex items-center justify-center text-center"
              >
                <span className="text-[#12324A] font-semibold">{provider}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-lg text-[#5F6C7B] mb-4">
              Don't see your insurance provider? Contact us to verify coverage.
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Checker Form */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border border-gray-200">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#12324A] mb-4">
                Check Your Coverage
              </h2>
              <p className="text-lg text-[#5F6C7B]">
                Verify your insurance coverage in seconds
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="insurance" className="text-[#12324A] font-medium mb-2 block">
                  Insurance Provider
                </Label>
                <select
                  id="insurance"
                  value={formData.insuranceProvider}
                  onChange={(e) => setFormData({ ...formData, insuranceProvider: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81] focus:ring-opacity-20 transition-all"
                  required
                >
                  <option value="">Select your insurance provider</option>
                  {insuranceProviders.map((provider) => (
                    <option key={provider} value={provider}>{provider}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="memberId" className="text-[#12324A] font-medium mb-2 block">
                  Member ID
                </Label>
                <Input
                  id="memberId"
                  type="text"
                  placeholder="Enter your member ID"
                  value={formData.memberId}
                  onChange={(e) => setFormData({ ...formData, memberId: e.target.value })}
                  className="border-2 border-gray-200 focus:border-[#0F4C81] rounded-xl py-6"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="state" className="text-[#12324A] font-medium mb-2 block">
                  State
                </Label>
                <Input
                  id="state"
                  type="text"
                  placeholder="Enter your state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="border-2 border-gray-200 focus:border-[#0F4C81] rounded-xl py-6"
                  required
                />
              </div>
              
              <Button
                type="submit"
                size="lg"
                className="w-full bg-[#0F4C81] hover:bg-[#12324A] text-white py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
              >
                Check Coverage
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Medicaid Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-6">
                Medicaid Coverage
              </h2>
              <p className="text-lg text-[#5F6C7B] mb-6 leading-relaxed">
                We accept Medicaid in select states. Medicaid coverage provides access to our full range of telehealth services at no cost to you (in most cases).
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <span className="font-semibold text-[#12324A] block mb-1">No copay required</span>
                    <span className="text-[#5F6C7B]">In most Medicaid plans, there's no out-of-pocket cost</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <span className="font-semibold text-[#12324A] block mb-1">Full service access</span>
                    <span className="text-[#5F6C7B]">All telehealth services available to Medicaid members</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <span className="font-semibold text-[#12324A] block mb-1">Easy verification</span>
                    <span className="text-[#5F6C7B]">We verify your coverage before your visit</span>
                  </div>
                </li>
              </ul>
              <Button
                size="lg"
                className="bg-[#0F4C81] hover:bg-[#12324A] text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
              >
                Check Medicaid Eligibility
              </Button>
            </div>
            <div className="bg-gradient-to-br from-[#F4F6F8] to-white rounded-2xl p-12 border border-gray-200">
              <h3 className="text-2xl font-bold text-[#12324A] mb-6">States We Serve</h3>
              <p className="text-[#5F6C7B] mb-6">
                Medicaid coverage availability varies by state. Currently accepting Medicaid in:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {["Alabama", "Arizona", "California", "Florida", "Georgia", "Illinois", "Michigan", "New York", "Ohio", "Texas", "+ More states"].map((state, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#1FB6B2]"></div>
                    <span className="text-[#5F6C7B]">{state}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              How Insurance Billing Works
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              Simple, transparent billing process
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-semibold text-[#12324A] mb-4">Verify Coverage</h3>
              <p className="text-[#5F6C7B]">
                We verify your insurance before your visit to confirm coverage and copay amount.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-semibold text-[#12324A] mb-4">Get Care</h3>
              <p className="text-[#5F6C7B]">
                Complete your virtual visit. Pay your copay online during or after the visit.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-semibold text-[#12324A] mb-4">We Bill Insurance</h3>
              <p className="text-[#5F6C7B]">
                We handle all insurance billing. You only pay your standard copay amount.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F4C81] to-[#12324A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Use Your Insurance Today
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Get quality healthcare using your existing coverage. No surprises, no hidden fees.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#0F4C81] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
          >
            Start a Visit Now
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Insurance;
